import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getStoreState, useAppStore, type AppState } from '../../store';
import { callAction } from '../../app/actions';
import { DOC_ID_FIELD_BY_KEY } from '../../lib/docs';
import type { DocId, DocKindKey, NodeId } from '../../lib/types';
import { MapNode } from './MapNode';
import { WireLayer } from './WireLayer';
import { mapContextOf } from './mapContext';
import { countStageRender } from './renderCount';
import { setDragOffset } from './dragOffset';
import { setStagePointer } from './stagePointer';
import { repaintWires } from './wireBus';
import {
  commitPositions,
  evaluateDocsExpand,
  hasPendingLayout,
  requestLayout,
  requestSettle,
  runPendingLayout,
  setStageMetrics,
  subscribeCamera,
  zoomAt,
} from './mapController';

/**
 * The map.
 *
 * This component owns the three things `render()` used to do at once and one thing it could
 * not do at all: the node list, the camera, the measurement pass, and the pointer.
 *
 * ── THE CIRCULARITY, BROKEN ────────────────────────────────────────────────────────────
 * A node's height is CSS-derived from how its text wraps, so the vanilla layout could only
 * work by rendering, measuring, and writing measured-derived positions back into the model —
 * `autoLayout()` opens with its own `render()` for exactly that reason, and `enterApp()` did
 * three of them on boot. The escape is to make measurement an INPUT rather than a step:
 *
 *     render (stored positions) -> measure -> layout(graph, sizes, opts) -> positions
 *
 * and to do the middle two in a LAYOUT EFFECT, which runs after the DOM exists and before the
 * browser paints. So a branch expanding still measures its freshly-revealed children at their
 * true height, exactly as the original did — but the intermediate state never reaches the
 * screen, and the pass is one paint rather than three renders and three measurement sweeps.
 *
 * The queue (`mapController.ts`) is why `autoLayout()` can be called before the nodes it is
 * about exist: the request is recorded, and drained here once they do.
 *
 * ── WHAT DOES NOT RE-RENDER ────────────────────────────────────────────────────────────
 * The camera is a `transform` written from a store subscription straight onto `#world` and
 * the wire layer's `<g>`. A pan re-renders nothing at all — not this component, not a node,
 * not a path — which is the assertion the plan asks for and `e2e/35-map.spec.ts` makes.
 *
 * A drag writes `left`/`top` on the dragged elements and asks the wire layer to repaint its
 * `d` attributes, both imperatively, on the same frame. State is committed on `pointerup`
 * ONLY, which is also where `resolveCollisions` / `maybeFlipSide` / `reparent` / `pushUndo`
 * run — the same boundary the original commits at, kept exactly.
 *
 * ── WHAT IS STILL THE VANILLA SCRIPT'S, AND WHY ────────────────────────────────────────
 * Creating, deleting and reparenting a node, the undo stack, the popup and the save path all
 * stay in `infostation.html` for now and are reached through named bridge actions. They are
 * not map RENDERING; they are the map's writes to the server and to the undo stack, and both
 * of those still live on the vanilla side of the migration. Every one of them is listed in
 * `connectReactBridge()`, and they go when the last of the vanilla script does.
 */

/** Which store values decide the SET of nodes on screen. Nothing here changes during a pan. */
function stageInputsOf(s: AppState) {
  return {
    nodes: s.nodes,
    edges: s.edges,
    perm: s.perm,
    tagEdges: s.tagEdges,
    forceOpen: s.forceOpen,
    layoutTick: s.layoutTick,
  };
}

export function MapStage({ host }: { host: HTMLElement }) {
  const inputs = useAppStore(useShallow(stageInputsOf));
  countStageRender();

  const worldRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  /**
   * The ids on screen, KEPT BY IDENTITY while the set itself does not change.
   *
   * `mapContextOf` recomputes for every new state object, so a plain memo would hand out a
   * fresh array on every vanilla `render()` — and a vanilla `render()` is a label edit, a tag
   * recolour, a doc title arriving, fifty call sites' worth of things that do not change WHICH
   * nodes are on screen. Holding the identity is what lets the measurement sweep below skip
   * (the visible set is the only thing a re-render can tell it about) and what keeps the node
   * list from being a fresh array of props each time.
   */
  const idsRef = useRef<readonly NodeId[]>([]);
  const visibleIds = useMemo(() => {
    const next = mapContextOf(getStoreState()).visibleIds;
    const prev = idsRef.current;
    if (prev.length === next.length && prev.every((id, i) => id === next[i])) return prev;
    idsRef.current = next;
    return next;
  }, [inputs]);

  /* ---- the camera: applied, never rendered -------------------------------------- */

  const applyCamera = useCallback((view: AppState['view']) => {
    const w = worldRef.current;
    if (w) w.style.transform = `translate(${view.x}px,${view.y}px) scale(${view.k})`;
  }, []);

  useLayoutEffect(() => {
    applyCamera(getStoreState().view);
  });
  useEffect(() => subscribeCamera(applyCamera), [applyCamera]);

  /* ---- measure, then lay out, before paint --------------------------------------- */

  /** The measurement sweep. One forced reflow for the whole map, never one per node. */
  const measure = useCallback((world: HTMLElement) => {
    // `offsetWidth`/`offsetHeight` rather than the observer's fractional box, because the
    // original measured with these and they round to whole pixels. A fractional height here
    // would move every node below it by a fraction — invisible, and different.
    const sizes: Record<NodeId, { w: number; h: number }> = {};
    for (const el of world.querySelectorAll<HTMLElement>('.node')) {
      const id = el.dataset.id;
      if (id) sizes[id] = { w: el.offsetWidth, h: el.offsetHeight };
    }
    getStoreState().replaceSizes(sizes);
    return sizes;
  }, []);

  /** The visible set the sizes in the store were measured from. */
  const measuredFor = useRef<readonly NodeId[] | null>(null);

  useLayoutEffect(() => {
    const world = worldRef.current;
    if (!world) return;

    // MEASURE ONLY WHEN A MEASUREMENT COULD HAVE CHANGED, which is narrower than "on every
    // render". This component re-renders on every vanilla `render()` — the store adopts fresh
    // graph containers each time, by design — and there are about fifty call sites. Sweeping
    // 147 nodes on each of them is a forced reflow per sync, for a map that may not even be on
    // screen: a full-screen modal is over it for most of the app's surface area.
    //
    // A size can only have changed for two reasons. The visible SET changed, which is this
    // render — so `visibleIds`' identity answers it. Or a node's CONTENT changed, which no
    // render of this component can know about and which the `ResizeObserver` below exists to
    // catch. The third case is a queued layout pass, which needs current sizes by definition.
    let sizes = getStoreState().sizes;
    if (measuredFor.current !== visibleIds || hasPendingLayout()) {
      measuredFor.current = visibleIds;
      sizes = measure(world);
    }
    runPendingLayout(sizes);

    if (!revealed && visibleIds.length) setRevealed(true);
  });

  /**
   * A shared observer for the size changes a render does not cause: a font finishing loading,
   * a locale switch relabelling every node, a doc pill arriving with its real title.
   *
   * It updates the SIZE STORE only. It deliberately does not trigger a layout pass, because
   * the original does not either — a label edit leaves the tree where it is until somebody
   * asks for a realign, and re-laying the map out from under them would be a new behaviour.
   */
  useEffect(() => {
    const world = worldRef.current;
    if (!world || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const w = worldRef.current;
        if (!w) return;
        measure(w);
        repaintWires();
      });
    });
    for (const el of world.querySelectorAll<HTMLElement>('.node')) ro.observe(el);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
    // Re-observing on every change of the visible set is what keeps new nodes covered.
  }, [visibleIds, measure]);

  /* ---- the pointer -------------------------------------------------------------- */

  useEffect(() => {
    const stage = host.parentElement ?? document.getElementById('stage');
    if (!stage) return;
    setStageMetrics(() => ({ width: stage.clientWidth, height: stage.clientHeight }));
    const handlers = installPointerHandlers(stage as HTMLElement);
    return handlers;
  }, [host]);

  /**
   * Disarming the Link tool drops whichever node was waiting for its pair. Only DISarming,
   * since V0.54: the right-click menu's "Link to…" arms the tool with the first end already
   * chosen, and a reset on arming would throw that choice away the moment it was made. A tool
   * armed from its button starts empty anyway — the last disarm cleared it.
   */
  const linkArmed = useAppStore((s) => s.linkArmed);
  useEffect(() => {
    if (!linkArmed) getStoreState().setLinkFromId(null);
  }, [linkArmed]);

  return (
    <>
      <svg id="wires">
        <WireLayer />
      </svg>
      <div
        id="world"
        ref={worldRef}
        // Hidden until the first measured layout has landed, so nodes are never seen at a
        // stale position and then jumping. In practice this is one commit that never paints.
        style={revealed ? undefined : { visibility: 'hidden' }}
      >
        {visibleIds.map((id) => (
          <MapNode key={id} id={id} />
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------------------ *
 * Pointer handling                                                                       *
 *                                                                                        *
 * Everything below runs OUTSIDE React. A `pointermove` is not a component: it reads the   *
 * camera and the dragged subtree, writes two style properties per node and asks for a     *
 * wire repaint, and routing that through React state would reconcile 147 nodes per mouse  *
 * movement. `getState()` is the read; `pointerup` is the only write.                      *
 * ------------------------------------------------------------------------------------ */

/**
 * What one node in the dragged subtree looked like before the drag.
 *
 * COLOUR, DEPTH AND SIDE, not just position — because a DROP can change all three across the
 * whole moved subtree. `reparent()` re-colours it to the new branch, re-bases every depth, and
 * mirrors it horizontally if the side flipped; `maybeFlipSide()` flips a top-level branch that
 * crossed the centre axis. An undo that put back only x and y would leave a branch the right
 * shape and the wrong colour, one column too deep, on the wrong side of the root.
 */
interface DragOrigin {
  id: NodeId;
  x: number;
  y: number;
  side: number;
  depth: number;
  color: string | null;
}

interface DragState {
  id: NodeId;
  /** Pointer position in world coordinates at `pointerdown`. */
  sx: number;
  sy: number;
  /** The dragged node and every descendant, as each was before the drag started. */
  group: DragOrigin[];
  ids: Set<NodeId>;
  moved: boolean;
  dx: number;
  dy: number;
  prevParent: NodeId | undefined;
  target: NodeId | null;
}

interface PanState {
  sx: number;
  sy: number;
  ox: number;
  oy: number;
  /** The pointer has moved since pointerdown — the pan has BEGUN, as opposed to a click. */
  moved?: boolean;
}

function installPointerHandlers(stage: HTMLElement): () => void {
  let drag: DragState | null = null;
  let pan: PanState | null = null;

  /** Screen point -> world point. The camera's inverse, and the original's `s2w()`. */
  const s2w = (cx: number, cy: number) => {
    const r = stage.getBoundingClientRect();
    const { view } = getStoreState();
    return { x: (cx - r.left - view.x) / view.k, y: (cy - r.top - view.y) / view.k };
  };

  const onPointerDown = (e: PointerEvent) => {
    // The SECONDARY button is the right-click menu's (V0.54) — `onContextMenu` below. Before
    // it, a right-press went down this path like any other: it opened the popup in view mode
    // and started a drag in edit mode, under the browser's own menu.
    if (e.button === 2) return;
    const target = e.target as HTMLElement | null;
    if (!target || !target.closest) return;
    const s = getStoreState();
    const nd = target.closest('.node') as HTMLElement | null;

    // The collapse toggle works in BOTH modes: it is navigation, not editing.
    const tog = target.closest('[data-toggle]') as HTMLElement | null;
    if (tog) {
      callAction('toggleCollapsed', tog.dataset.toggle);
      e.stopPropagation();
      return;
    }

    // A documentation pill, in either of its two shapes — the small blob and the zoomed-in
    // row carry the same markers, so one branch covers both.
    const bean = target.closest(
      '[data-opendoc-kind],[data-opendoc-custom],[data-opendoc-more]',
    ) as HTMLElement | null;
    if (bean && nd) {
      e.stopPropagation();
      openBean(bean, nd.dataset.id ?? '');
      return;
    }

    // A dotted PLANNED pill (V0.42) creates its page for whoever may — the action refuses and
    // says so for everyone else. Fires in BOTH modes, like opening a doc does.
    const createPill = target.closest('[data-createdoc-kind]') as HTMLElement | null;
    if (createPill && nd) {
      e.stopPropagation();
      const nodeId = nd.dataset.id ?? '';
      const kind = createPill.dataset.createdocKind ?? '';
      if (nodeId && kind) callAction('createDocForNode', nodeId, kind);
      return;
    }

    if (!s.editing) {
      // View only: a click on a node opens its read-only popup; anywhere else pans.
      if (nd && nd.dataset.id && !s.nodes[nd.dataset.id]?.root) {
        // The reader's open (V0.47): centre the node, then it takes its seat in the popup.
        callAction('focusPopup', nd.dataset.id);
        return;
      }
      startPan(e);
      return;
    }

    /* --- editing --- */
    // Structural acts — delete, Ctrl-add, drag — are EDIT_MAP's (V0.37). A participant in
    // edit mode without the brick keeps navigation, the popup and the pan; the affordances
    // themselves are not drawn for them (the delete cross, the add cursor), so these checks
    // are the backstop rather than the message.
    const mapEditable = mapContextOf(s).canEditMap;
    const del = target.closest('[data-del]') as HTMLElement | null;
    if (del && mapEditable) {
      callAction('delNode', del.dataset.del);
      e.stopPropagation();
      return;
    }

    // A "Move to…" from the right-click menu is waiting for its parent (V0.54): this click is
    // it — a node becomes the new parent, anywhere else puts the node down where it was.
    if (callAction('takeMoveClick', nd?.dataset.id ?? null)) {
      e.stopPropagation();
      return;
    }

    if (s.linkArmed && nd && nd.dataset.id) {
      const id = nd.dataset.id;
      if (!s.linkFromId) s.setLinkFromId(id);
      else if (s.linkFromId !== id) {
        callAction('linkNodes', s.linkFromId, id);
        s.setLinkFromId(null);
      }
      return;
    }

    // A dotted cross-link opens its colour/delete menu, but only while Linking is armed.
    if (s.linkArmed) {
      const le = target.closest('[data-link]') as HTMLElement | null;
      if (le) {
        callAction('openLinkMenu', Number(le.dataset.link), e.clientX, e.clientY);
        e.stopPropagation();
        return;
      }
    }

    if (s.ctrlAdd && mapEditable) {
      if (nd && nd.dataset.id) {
        callAction('addChild', nd.dataset.id);
        return;
      }
      const p = s2w(e.clientX, e.clientY);
      callAction('createNodeAt', p.x, p.y - 21, e.shiftKey ? -1 : 1);
      return;
    }

    if (nd && nd.dataset.id && mapEditable) {
      startDrag(e, nd.dataset.id);
      return;
    }
    startPan(e);
  };

  const startPan = (e: PointerEvent) => {
    const { view } = getStoreState();
    pan = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    stage.classList.add('grabbing');
  };

  const startDrag = (e: PointerEvent, id: NodeId) => {
    const s = getStoreState();
    const ctx = mapContextOf(s);
    const p = s2w(e.clientX, e.clientY);

    const ids = new Set<NodeId>([id, ...ctx.graph.descendants(id)]);
    const group: DragOrigin[] = [...ids]
      .filter((i) => s.positions[i])
      .map((i) => ({
        id: i,
        x: s.positions[i].x,
        y: s.positions[i].y,
        side: s.positions[i].side,
        depth: s.positions[i].depth,
        color: s.nodes[i]?.color ?? null,
      }));

    drag = {
      id,
      sx: p.x,
      sy: p.y,
      group,
      ids,
      moved: false,
      dx: 0,
      dy: 0,
      prevParent: s.edges[id],
      target: null,
    };
    s.setDragNodeId(id);
    setDragOffset({ ids, dx: 0, dy: 0 });
  };

  const onPointerMove = (e: PointerEvent) => {
    if (drag) {
      const p = s2w(e.clientX, e.clientY);
      const ddx = p.x - drag.sx;
      const ddy = p.y - drag.sy;

      // NOTHING MOVES UNTIL THE POINTER DOES (V0.42.4).
      //
      // A pointerdown on a node is not yet a drag — it is also the first half of the click
      // that opens the node, and of the double-click that opens it for editing. Until the 2px
      // threshold is crossed this handler must leave the map exactly where it is, because
      // BOTH things below move it out from under the cursor:
      //
      //   - the collapse. A drag operates in CANONICAL space so collision resolution never
      //     reasons about two coordinate systems at once, and the expanded documentation rows
      //     have to go for that. It used to happen in `startDrag()`, i.e. on pointerdown, so
      //     merely pressing a node re-packed the map — measured at 87px for a node one row
      //     deep. The second click of a double-click then landed on empty space and the node
      //     could not be opened at all.
      //   - the style writes below, which are `g.y + ddy` off the CANONICAL origin captured at
      //     pointerdown. They teleport the node by the same amount on their own, so gating the
      //     collapse alone would not have been enough.
      //
      // Both now wait for a real drag. A click moves nothing, and what a drag does afterwards
      // is unchanged — `moved` still flips at the same 2px, and the commit still reads the
      // canonical origins, which the collapse never touched.
      if (!drag.moved) {
        if (Math.abs(ddx) <= 2 && Math.abs(ddy) <= 2) return;
        drag.moved = true;
        const st = getStoreState();
        if (st.docsExpanded) {
          st.setDocsExpanded(false);
          st.setDisplayY({});
        }
      }
      drag.dx = ddx;
      drag.dy = ddy;

      // Two style writes per node and one wire repaint. No React state, no reconciliation.
      const world = document.getElementById('world');
      for (const g of drag.group) {
        const el = world?.querySelector<HTMLElement>(`.node[data-id="${cssEscape(g.id)}"]`);
        if (el) {
          el.style.left = `${g.x + ddx}px`;
          el.style.top = `${g.y + ddy}px`;
        }
      }
      setDragOffset({ ids: drag.ids, dx: ddx, dy: ddy });
      repaintWires();

      if (drag.moved) {
        const next = findDropTarget(e.clientX, e.clientY, drag.ids);
        if (next !== drag.target) {
          drag.target = next;
          getStoreState().setDropTargetId(next);
        }
      }
      return;
    }
    if (pan) {
      if (!pan.moved) {
        pan.moved = true;
        // The hint steps aside at the FIRST movement of a pan (V0.53.4) — not at pointerdown,
        // which is also the first half of a click on empty canvas, and not per frame.
        callAction('hideHint');
      }
      getStoreState().panTo(pan.ox + (e.clientX - pan.sx), pan.oy + (e.clientY - pan.sy));
      repaintWires();
    }
  };

  const onPointerUp = () => {
    if (drag) {
      const d = drag;
      drag = null;
      const s = getStoreState();
      s.setDragNodeId(null);
      s.setDropTargetId(null);
      setDragOffset(null);

      if (d.moved) {
        // Commit first, then decide what the drop meant: `reparent()` reads the dragged
        // node's x, and `maybeFlipSide()` reads its side and x.
        const next = { ...s.positions };
        for (const g of d.group) {
          const p = s.positions[g.id];
          if (p) next[g.id] = { ...p, x: g.x + d.dx, y: g.y + d.dy };
        }
        // STRUCTURAL: a drag is the clearest statement there is that these coordinates are
        // meant for everybody. This is what arms the position half of the next delta.
        commitPositions(next, 'structural');
      }

      if (d.moved && d.target && s.edges[d.id] !== d.target) {
        callAction('reparent', d.id, d.target);
      } else if (d.moved) {
        if (maybeFlipSide(d.id)) requestLayout('structural');
        else requestSettle(d.id, 'structural');
      }

      if (d.moved) {
        const label = s.nodes[d.id]?.label ?? '';
        const group = d.group;
        const prevParent = d.prevParent;
        // The whole restore goes through ONE vanilla action rather than being split between
        // the two sides. Colour is not a position and never was — putting it back from here
        // would make this a second writer of a field the vanilla script owns — and the edge
        // and the save are its too. `undoDrag` is the original's undo body, unchanged.
        callAction('pushUndo', `move "${label}"`, () => callAction('undoDrag', d.id, prevParent, group));
      }

      callAction('save');
      // Restore the expanded rows immediately if the camera is still past the threshold.
      evaluateDocsExpand();
    }
    if (pan) {
      pan = null;
      stage.classList.remove('grabbing');
      // A pan is a camera move: durable prefs and this user's view record, and NOTHING on the
      // wire. This line issuing a full structural PUT is the reported bug (plan Part 1).
      // QUIET since V0.49.2 — moving the view changes nothing, so there is nothing for the
      // "Saved" badge to be about. The view record is still written.
      callAction('saveLocal', { quiet: true });
    }
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const r = stage.getBoundingClientRect();
    zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.1 : 0.9);
    repaintWires();
    callAction('saveLocal', { quiet: true }); // a wheel notch is view-only — see the pan above
  };

  const onDoubleClick = (e: MouseEvent) => {
    const nd = (e.target as HTMLElement | null)?.closest?.('.node') as HTMLElement | null;
    const id = nd?.dataset.id;
    if (!id) return;
    if (getStoreState().nodes[id]?.root) return;
    callAction('focusPopup', id);
  };

  /**
   * THE RIGHT-CLICK (V0.54): decide what was clicked — a documentation pill, a node, a dotted
   * link, or the empty map — and open that menu at the pointer. The same delegation the
   * pointerdown above uses, reading the same markers, so a pill is a pill here exactly when a
   * click would open its page.
   *
   * Left alone, so the browser's menu appears: Shift held (the escape hatch — Inspect, copy
   * text), a text field (a rename in progress keeps its cut/copy/paste), and a gesture still
   * under way.
   */
  const onContextMenu = (e: MouseEvent) => {
    if (e.shiftKey) return;
    const target = e.target as HTMLElement | null;
    if (!target || !target.closest) return;
    if (target.closest('input, textarea, [contenteditable="true"]')) return;
    e.preventDefault();
    if (drag || pan) return;
    const s = getStoreState();
    // A pending "Move to…" is put down by any other gesture, the right-click included.
    callAction('cancelMove');
    const nd = target.closest('.node') as HTMLElement | null;
    const nodeId = nd?.dataset.id;
    const bean = target.closest('[data-opendoc-kind],[data-opendoc-custom]') as HTMLElement | null;
    const docId = bean && nodeId ? beanDocId(bean, nodeId) : null;
    if (docId && nodeId) {
      callAction('openContextMenu', { kind: 'pill', nodeId, docId }, e.clientX, e.clientY);
      return;
    }
    if (nodeId && s.nodes[nodeId]) {
      callAction('openContextMenu', { kind: 'node', nodeId }, e.clientX, e.clientY);
      return;
    }
    const le = target.closest('[data-link]') as HTMLElement | null;
    if (le) {
      callAction('openContextMenu', { kind: 'link', index: Number(le.dataset.link) }, e.clientX, e.clientY);
      return;
    }
    const p = s2w(e.clientX, e.clientY);
    callAction('openContextMenu', { kind: 'map', x: p.x, y: p.y }, e.clientX, e.clientY);
  };

  /** Where the pointer is over the stage, for Ctrl+V onto the empty map. */
  const onStageMove = (e: PointerEvent) => setStagePointer({ x: e.clientX, y: e.clientY });
  const onStageLeave = () => setStagePointer(null);

  stage.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  stage.addEventListener('wheel', onWheel, { passive: false });
  stage.addEventListener('dblclick', onDoubleClick);
  stage.addEventListener('contextmenu', onContextMenu);
  stage.addEventListener('pointermove', onStageMove);
  stage.addEventListener('pointerleave', onStageLeave);

  return () => {
    stage.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    stage.removeEventListener('wheel', onWheel);
    stage.removeEventListener('dblclick', onDoubleClick);
    stage.removeEventListener('contextmenu', onContextMenu);
    stage.removeEventListener('pointermove', onStageMove);
    stage.removeEventListener('pointerleave', onStageLeave);
  };
}

/**
 * Which node is under the pointer, excluding the dragged one and its subtree.
 *
 * `elementsFromPoint` rather than arithmetic over the position map, deliberately: it is
 * cheap, it is correct about overlap and z-order without reimplementing either, and
 * hit-testing is the one legitimate DOM read left in the map.
 */
function findDropTarget(cx: number, cy: number, banned: ReadonlySet<NodeId>): NodeId | null {
  for (const el of document.elementsFromPoint(cx, cy)) {
    const nd = (el as HTMLElement).closest?.('.node') as HTMLElement | null;
    const id = nd?.dataset.id;
    if (id && !banned.has(id)) return id;
  }
  return null;
}

/**
 * A top-level branch dragged past the centre axis flips to the other side.
 *
 * Only the branch node itself needs changing: the layout pass that follows re-derives every
 * descendant's side and position from it.
 */
function maybeFlipSide(id: NodeId): boolean {
  const s = getStoreState();
  const n = s.nodes[id];
  const p = s.positions[id];
  if (!n || n.root || !p) return false;
  if ((p.depth || 1) !== 1) return false;
  const curSide = (p.side || 1) >= 0 ? 1 : -1;
  const newSide = p.x >= 0 ? 1 : -1;
  if (newSide === curSide) return false;

  const ctx = mapContextOf(s);
  const next = { ...s.positions };
  for (const i of [id, ...ctx.graph.descendants(id)]) {
    if (next[i]) next[i] = { ...next[i], side: newSide };
  }
  // Reached only from the drag commit, so structural like the commit that called it.
  commitPositions(next, 'structural');
  return true;
}

/** Open the page a documentation pill points at, without going through the popup. */
function openBean(bean: HTMLElement, nodeId: NodeId): void {
  if (!getStoreState().nodes[nodeId]) return;
  if (bean.dataset.opendocMore) {
    callAction('focusPopup', nodeId);
    return;
  }
  const docId = beanDocId(bean, nodeId);
  // From the map (V0.47): the camera centres the node, then the node crosses into the page.
  // `openNodeDoc` refuses a kind hidden from this group, as this function used to itself.
  if (docId) callAction('openNodeDoc', nodeId, docId);
}

/**
 * The page a pill's markers name: a fixed slot's doc id read off the node, or a custom page's
 * id straight off the pill. Null when the slot is empty now (the page was detached since the
 * pill was drawn). A click and a right-click read a pill through this one function.
 */
function beanDocId(bean: HTMLElement, nodeId: NodeId): DocId | null {
  const n = getStoreState().nodes[nodeId];
  if (!n) return null;
  const key = bean.dataset.opendocKind as DocKindKey | undefined;
  return (key ? (n[DOC_ID_FIELD_BY_KEY[key]] as DocId | null) : bean.dataset.opendocCustom) || null;
}

/** `CSS.escape`, with a fallback for the ids this map actually uses (cuids: word characters). */
function cssEscape(v: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v;
}
